import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutGrid, List, Search, X } from "lucide-react";
import { BrandCard } from "@/components/BrandCard";
import { CATEGORIES } from "@/lib/api";
import { useBrands } from "@/lib/hooks";
import { cn } from "@/lib/utils";

const LETTERS = ["All", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""), "#"];
const SORTS = [["az", "A–Z"], ["updated", "Recently updated"], ["recent", "Recently added"], ["downloads", "Most downloaded"]];

const Pill = ({ active, children, ...props }) => (
  <button type="button" {...props}
    className={cn("shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,color,border-color] duration-300",
      active ? "border-ph-lime bg-ph-lime text-ph-navy" : "border-ph-line bg-ph-surface text-ph-muted hover:border-ph-accent/50 hover:text-ph-ink")}>
    {children}
  </button>
);

export function BrandsExplorer({ initialCategory = "", initialQuery = "", limit, followMode = false, selected, onToggle, onShownChange }) {
  const { brands, loading, error } = useBrands();
  const [q, setQ] = useState(initialQuery);
  const [category, setCategory] = useState(initialCategory);
  const [letter, setLetter] = useState("All");
  const [sort, setSort] = useState("az");
  const [withLists, setWithLists] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("ph_brands_view") || "grid");
  const pickView = (v) => { setView(v); localStorage.setItem("ph_brands_view", v); };

  const available = useMemo(() => new Set(brands.map((b) => (/[A-Z]/i.test(b.name[0]) ? b.name[0].toUpperCase() : "#"))), [brands]);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const out = brands.filter((b) => {
      const first = /[A-Z]/i.test(b.name[0]) ? b.name[0].toUpperCase() : "#";
      return (!term || b.name.toLowerCase().includes(term) || (b.categories ?? []).some((c) => c.toLowerCase().includes(term)))
        && (!category || (b.categories ?? []).includes(category))
        && (letter === "All" || first === letter)
        && (!withLists || (b.doc_count ?? 0) > 0);
    });
    const cmp = { az: (a, b) => a.name.localeCompare(b.name), updated: (a, b) => (b.last_price_change || "").localeCompare(a.last_price_change || "") || a.name.localeCompare(b.name), recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""), downloads: (a, b) => (b.download_count ?? 0) - (a.download_count ?? 0) || a.name.localeCompare(b.name) }[sort];
    return out.sort(cmp);
  }, [brands, q, category, letter, sort, withLists]);
  const filtered = q || category || letter !== "All" || withLists;
  const reset = () => { setQ(""); setCategory(""); setLetter("All"); setWithLists(false); };
  const shown = useMemo(() => (limit ? list.slice(0, limit) : list), [list, limit]);
  useEffect(() => { onShownChange?.(shown); }, [shown, onShownChange]);

  return (
    <div data-testid="brands-explorer">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ph-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter brands..." aria-label="Filter brands by name or category" enterKeyHint="search" data-testid="brands-filter-input"
            className="h-12 w-full rounded-full border border-ph-line bg-ph-surface pl-12 pr-10 text-sm text-ph-ink outline-none transition-[border-color,box-shadow] placeholder:text-ph-muted focus:border-ph-accent/50 focus:shadow-[0_0_0_4px_rgb(var(--ph-accent)/0.12)]" />
          {q && <button type="button" onClick={() => setQ("")} data-testid="brands-filter-clear" aria-label="Clear filter" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-ph-muted hover:bg-ph-raised"><X className="h-4 w-4" /></button>}
        </div>
        <div className="flex gap-2">
        <div className="flex flex-1 gap-1 overflow-x-auto rounded-full border border-ph-line bg-ph-surface p-1 ph-noscroll" role="tablist">
          {SORTS.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setSort(key)} data-testid={`brands-sort-${key}`}
              className={cn("relative shrink-0 rounded-full px-3 py-2 text-xs font-semibold sm:px-4 sm:text-sm", sort === key ? "text-ph-navy" : "text-ph-muted hover:text-ph-ink")}>
              {sort === key && <motion.span layoutId="sort-pill" className="absolute inset-0 rounded-full bg-ph-lime" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
              <span className="relative">{label}</span>
            </button>
          ))}
        </div>
        <div className="flex shrink-0 gap-1 rounded-full border border-ph-line bg-ph-surface p-1" aria-label="Layout">
          {[["grid", LayoutGrid], ["list", List]].map(([v, Icon]) => (
            <button key={v} type="button" onClick={() => pickView(v)} aria-pressed={view === v} aria-label={`${v} view`} data-testid={`${v}-view-toggle`}
              className={cn("grid h-9 w-9 place-items-center rounded-full transition-[background-color,color]", view === v ? "bg-ph-raised text-ph-accent" : "text-ph-muted hover:text-ph-ink")}>
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        </div>
      </div>

      <div className="ph-noscroll -mx-5 mt-5 flex gap-2 overflow-x-auto px-5 pb-1 md:mx-0 md:flex-wrap md:px-0" data-lenis-prevent>
        <Pill active={!category} onClick={() => setCategory("")} data-testid="brands-category-all">All categories</Pill>
        {CATEGORIES.map((c) => (
          <Pill key={c} active={category === c} onClick={() => setCategory(c)} data-testid={`brands-category-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</Pill>
        ))}
      </div>

      <div className="ph-noscroll -mx-5 mt-5 flex gap-1 overflow-x-auto px-5 md:mx-0 md:px-0" data-lenis-prevent data-testid="brands-az-nav">
        {LETTERS.map((l) => {
          const enabled = l === "All" || available.has(l);
          return (
            <button key={l} type="button" disabled={!enabled} onClick={() => setLetter(l)} data-testid={`brand-directory-az-letter-${l === "#" ? "num" : l.toLowerCase()}`}
              className={cn("h-9 shrink-0 rounded-lg px-2.5 font-mono-ph text-xs font-semibold transition-colors",
                letter === l ? "bg-ph-lime text-ph-navy" : enabled ? "text-ph-ink hover:bg-ph-raised" : "cursor-not-allowed text-ph-muted/40")}>
              {l}
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <p className="font-mono-ph text-xs uppercase tracking-[0.15em] text-ph-muted" data-testid="brands-result-count" aria-live="polite">
          {loading ? "Loading brands…" : error ? "" : `${list.length} ${list.length === 1 ? "brand" : "brands"}`}
        </p>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-ph-line bg-ph-surface px-3 py-1.5 text-xs font-medium text-ph-ink">
          <input type="checkbox" checked={withLists} onChange={(e) => setWithLists(e.target.checked)} data-testid="brands-filter-has-lists" className="h-4 w-4 accent-[#6366F1]" />
          Only brands with price lists
        </label>
        {filtered && <button type="button" onClick={reset} data-testid="brands-clear-all-filters" className="text-xs font-semibold text-ph-accent hover:underline">Clear all filters</button>}
      </div>

      {loading && (
        <div className={cn("mt-5 grid gap-4", view === "list" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4")} data-testid="brands-loading">
          {Array.from({ length: view === "list" ? 4 : 8 }, (_, i) => <div key={i} className={cn("ph2-shimmer rounded-3xl", view === "list" ? "h-20" : "h-56")} />)}
        </div>
      )}
      {error && !loading && (
        <div className="mt-6 rounded-3xl border border-red-400/40 bg-red-500/5 p-10 text-center" role="alert" data-testid="brands-error">
          <p className="font-display text-xl font-bold text-ph-ink">We couldn't load the brand list</p>
          <p className="mt-1 text-sm text-ph-muted">This is a connection problem, not an empty directory. Please try again.</p>
          <button type="button" onClick={() => window.location.reload()} data-testid="brands-retry" className="mt-4 rounded-full bg-ph-lime px-5 py-2.5 text-sm font-semibold text-ph-navy">Try again</button>
        </div>
      )}

      <motion.div layout className={cn("mt-5 grid gap-4", view === "list" ? "grid-cols-1 gap-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4")} data-testid={`brands-${view}`}>
        <AnimatePresence initial={false}>
          {shown.map((b, i) => (
            <motion.div key={b.id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.04, ease: [0.22, 1, 0.36, 1] }}>
              <BrandCard brand={b} view={view} selectable={followMode} selected={!!selected?.has(b.id)} onToggle={onToggle} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {!loading && !error && list.length === 0 && (
        <div className="mt-6 rounded-3xl border border-dashed border-ph-line p-12 text-center" data-testid="brands-empty">
          <p className="font-display text-2xl font-bold text-ph-ink">{brands.length ? "No brands match these filters" : "No brands listed yet"}</p>
          {brands.length > 0 && <button type="button" onClick={reset} data-testid="brands-reset-filters" className="mt-4 text-sm font-semibold text-ph-accent hover:underline">Reset filters</button>}
        </div>
      )}
    </div>
  );
}
